import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { bookingsApi, propertiesApi, leadsApi, unitsApi } from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { DeleteButton } from '../components/DeleteButton';
import BookingDrawer from '../components/BookingDrawer';
import { toast } from 'sonner';

const STAGE_COLOR = {
  Active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
  Completed: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const defaultSchedule = (agreed) => {
  const today = new Date();
  const addMonths = (m) => {
    const d = new Date(today); d.setMonth(d.getMonth() + m);
    return d.toISOString().slice(0, 10);
  };
  const a = Number(agreed || 0);
  return [
    { milestone: 'Booking Amount (10%)',   due_date: addMonths(0),  amount: Math.round(a * 0.10) },
    { milestone: 'On Agreement (20%)',     due_date: addMonths(1),  amount: Math.round(a * 0.20) },
    { milestone: 'Plinth (15%)',           due_date: addMonths(4),  amount: Math.round(a * 0.15) },
    { milestone: 'Slab Casting (25%)',     due_date: addMonths(8),  amount: Math.round(a * 0.25) },
    { milestone: 'Brickwork (15%)',        due_date: addMonths(12), amount: Math.round(a * 0.15) },
    { milestone: 'On Possession (15%)',    due_date: addMonths(18), amount: Math.round(a * 0.15) },
  ];
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/**
 * FUZZY DATE MATCHER
 * Protects against strict JS Timezone shifting and handles Dummy API data
 */
const isDateToday = (apiDateString) => {
  if (!apiDateString) return false;
  const str = String(apiDateString).trim().toLowerCase();
  
  if (str === 'today') return true;

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const html5Date = `${yyyy}-${mm}-${dd}`;
  
  if (str.includes(html5Date)) return true;

  try {
    const parsed = new Date(apiDateString);
    if (!isNaN(parsed.getTime())) {
      return parsed.toDateString() === today.toDateString();
    }
  } catch (e) {
    // Ignore invalid formats
  }
  return false;
};

const BookingFormDialog = ({ trigger, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState({
    lead_id: '', customer_name: '', customer_phone: '', customer_email: '',
    project_id: '', unit_id: '', agreed_value: 0,
    booking_date: new Date().toISOString().slice(0, 10),
    kyc: { pan: '', aadhaar: '', address: '', dob: '', occupation: '', co_applicant_name: '', co_applicant_phone: '' },
    bank_name: '', bank_branch: '', bank_employee_name: '', bank_employee_phone: '',
    loan_status: 'Not Applied', notes: '',
    payment_schedule: [],
  });

  useEffect(() => {
    if (open) {
      setStep(1);
      (async () => {
        try {
          const [l, p] = await Promise.all([leadsApi.list(), propertiesApi.list()]);
          setLeads(l); setProjects(p);
        } catch (e) {
          console.error('Failed to load leads/projects for booking form', e);
          toast.error('Failed to load leads & projects');
        }
      })();
    }
  }, [open]);

  useEffect(() => {
    if (!form.project_id) { setUnits([]); return; }
    (async () => {
      try {
        const u = await unitsApi.list({ project_id: form.project_id, status: 'Available' });
        setUnits(u);
      } catch (e) {
        console.error('Failed to load available units', e);
        toast.error('Failed to load units for this project');
      }
    })();
  }, [form.project_id]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateKyc = (k, v) => setForm(f => ({ ...f, kyc: { ...f.kyc, [k]: v } }));

  const availableLeads = useMemo(() => leads.filter(l => l.status !== 'Booked').slice(0, 100), [leads]);

  const onLeadPick = (id) => {
    const l = leads.find(x => x.id === id);
    if (!l) return;
    const proj = projects.find(p => p.name === l.property);
    setForm(f => ({
      ...f,
      lead_id: id,
      customer_name: l.name,
      customer_phone: l.phone,
      customer_email: l.email || '',
      project_id: proj?.id || f.project_id,
    }));
  };

  const onAgreedValueChange = (v) => {
    const num = Number(v) || 0;
    setForm(f => ({ ...f, agreed_value: num, payment_schedule: defaultSchedule(num) }));
  };

  const updateSched = (idx, key, val) => {
    setForm(f => ({
      ...f,
      payment_schedule: f.payment_schedule.map((row, i) =>
        i === idx ? { ...row, [key]: key === 'amount' ? Number(val) || 0 : val } : row
      ),
    }));
  };

  const addSchedRow = () => {
    setForm(f => ({
      ...f,
      payment_schedule: [...f.payment_schedule, { milestone: '', due_date: new Date().toISOString().slice(0, 10), amount: 0 }],
    }));
  };

  const removeSchedRow = (idx) => {
    setForm(f => ({ ...f, payment_schedule: f.payment_schedule.filter((_, i) => i !== idx) }));
  };

  const submit = async () => {
    if (!form.customer_name || !form.customer_phone) { toast.error('Customer name & phone required'); return; }
    if (!form.project_id || !form.unit_id) { toast.error('Pick project and unit'); return; }
    if (!form.agreed_value || form.agreed_value <= 0) { toast.error('Enter agreed value'); return; }
    if (!form.payment_schedule.length) { toast.error('Add at least one installment'); return; }
    setSaving(true);
    try {
      const saved = await bookingsApi.create(form);
      toast.success(`Booking ${saved.id} created — Unit ${saved.unit_no} locked`);
      onSaved && onSaved(saved);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create booking');
    } finally { setSaving(false); }
  };

  const scheduleTotal = form.payment_schedule.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="booking-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New Booking — Step {step} of 4</DialogTitle>
          <DialogDescription>Convert a lead into a customer. Step {step}: {['Customer & Lead', 'Project & Unit', 'KYC & Bank', 'Payment Schedule'][step - 1]}.</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div className="sm:col-span-2">
              <Label>From Lead (optional)</Label>
              <Select value={form.lead_id} onValueChange={onLeadPick}>
                <SelectTrigger data-testid="booking-lead-select"><SelectValue placeholder="Pick a lead to autofill customer details..." /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {availableLeads.map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.name} • {l.phone} • {l.property || '—'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Customer Name *</Label><Input data-testid="booking-customer-name" value={form.customer_name} onChange={e => update('customer_name', e.target.value)} /></div>
            <div><Label>Phone *</Label><Input data-testid="booking-customer-phone" value={form.customer_phone} onChange={e => update('customer_phone', e.target.value)} /></div>
            <div><Label>Email</Label><Input value={form.customer_email} onChange={e => update('customer_email', e.target.value)} /></div>
            <div><Label>Booking Date</Label><Input type="date" value={form.booking_date} onChange={e => update('booking_date', e.target.value)} /></div>
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div>
              <Label>Project *</Label>
              <Select value={form.project_id} onValueChange={(v) => update('project_id', v)}>
                <SelectTrigger data-testid="booking-project-select"><SelectValue placeholder="Pick project..." /></SelectTrigger>
                <SelectContent>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Available Unit *</Label>
              <Select value={form.unit_id} onValueChange={(v) => update('unit_id', v)} disabled={!form.project_id}>
                <SelectTrigger data-testid="booking-unit-select"><SelectValue placeholder={form.project_id ? 'Pick unit...' : 'Pick project first'} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {units.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No available units. Inventory is fully booked.</div>}
                  {units.map(u => <SelectItem key={u.id} value={u.id}>{u.unit_no} • {u.area || '—'} • {u.type || ''}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">Only Available units shown. Selecting and saving locks this unit.</div>
            </div>
            <div className="sm:col-span-2">
              <Label>Agreed Value (₹) *</Label>
              <Input type="number" data-testid="booking-agreed-value" value={form.agreed_value} onChange={e => onAgreedValueChange(e.target.value)} placeholder="e.g. 9500000" />
              <div className="text-[11px] text-slate-500 mt-1">A 6-milestone default payment schedule will be auto-generated; you can fine-tune in the last step.</div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 mt-2">
            <div>
              <div className="text-xs uppercase text-slate-500 tracking-wider mb-2">KYC</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>PAN</Label><Input value={form.kyc.pan} onChange={e => updateKyc('pan', e.target.value)} placeholder="ABCDE1234F" /></div>
                <div><Label>Aadhaar</Label><Input value={form.kyc.aadhaar} onChange={e => updateKyc('aadhaar', e.target.value)} placeholder="XXXX XXXX XXXX" /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={form.kyc.dob} onChange={e => updateKyc('dob', e.target.value)} /></div>
                <div><Label>Occupation</Label><Input value={form.kyc.occupation} onChange={e => updateKyc('occupation', e.target.value)} /></div>
                <div className="sm:col-span-2"><Label>Address</Label><Textarea rows={2} value={form.kyc.address} onChange={e => updateKyc('address', e.target.value)} /></div>
                <div><Label>Co-applicant Name</Label><Input value={form.kyc.co_applicant_name} onChange={e => updateKyc('co_applicant_name', e.target.value)} /></div>
                <div><Label>Co-applicant Phone</Label><Input value={form.kyc.co_applicant_phone} onChange={e => updateKyc('co_applicant_phone', e.target.value)} /></div>
              </div>
            </div>
            <div className="pt-3 border-t border-slate-100">
              <div className="text-xs uppercase text-slate-500 tracking-wider mb-2">Bank / Home Loan</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={e => update('bank_name', e.target.value)} placeholder="e.g. HDFC" /></div>
                <div><Label>Branch</Label><Input value={form.bank_branch} onChange={e => update('bank_branch', e.target.value)} /></div>
                <div><Label>Bank Employee</Label><Input value={form.bank_employee_name} onChange={e => update('bank_employee_name', e.target.value)} /></div>
                <div><Label>Employee Phone</Label><Input value={form.bank_employee_phone} onChange={e => update('bank_employee_phone', e.target.value)} /></div>
                <div className="sm:col-span-2">
                  <Label>Loan Status</Label>
                  <Select value={form.loan_status} onValueChange={(v) => update('loan_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Not Applied', 'Logged In', 'Query Raised', 'Sanctioned', 'Disbursed', 'Rejected'].map(s =>
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-slate-500">
                <span className="uppercase tracking-wider">Schedule Total</span>
                <span className="ml-2 font-semibold text-slate-700">{inr(scheduleTotal)}</span>
                {scheduleTotal !== Number(form.agreed_value) && (
                  <span className="ml-2 text-rose-600">(differs from agreed {inr(form.agreed_value)})</span>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={addSchedRow} data-testid="booking-add-row">
                <Icons.Plus className="w-4 h-4 mr-1" />Add Milestone
              </Button>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-[11px] uppercase text-slate-500">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Milestone</th>
                    <th className="px-3 py-2">Due Date</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {form.payment_schedule.map((row, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2"><Input value={row.milestone} onChange={e => updateSched(i, 'milestone', e.target.value)} /></td>
                      <td className="px-3 py-2"><Input type="date" value={row.due_date} onChange={e => updateSched(i, 'due_date', e.target.value)} /></td>
                      <td className="px-3 py-2"><Input type="number" className="text-right" value={row.amount} onChange={e => updateSched(i, 'amount', e.target.value)} /></td>
                      <td className="px-3 py-2 text-right">
                        <Button size="icon" variant="ghost" onClick={() => removeSchedRow(i)}><Icons.Trash2 className="w-4 h-4 text-rose-500" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 flex justify-between">
          <div>
            {step > 1 && <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            {step < 4
              ? <Button data-testid="booking-next-btn" className="bg-brand-navy hover:bg-[#13294A] text-white" onClick={() => setStep(s => s + 1)}>Next</Button>
              : <Button data-testid="booking-save-btn" className="bg-brand-navy hover:bg-[#13294A] text-white" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Create Booking'}</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Bookings = () => {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeBookingId, setActiveBookingId] = useState(null);

  // Router Driven Filter States
  const [applyTodayFilter, setApplyTodayFilter] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);

  const fetchAll = async () => {
    try {
      const list = await bookingsApi.list();
      setData(list);
    } catch (e) { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  };
  
  useEffect(() => { fetchAll(); }, []);

  // Intercept routing payload
  useEffect(() => {
    if (location.state?.filterStatus) {
      setStatusFilter(location.state.filterStatus);
    } else {
      setStatusFilter('all');
    }
    
    setApplyTodayFilter(location.state?.filterDate === 'today');
    setUsedFallback(false);
  }, [location.state]);

  const filtered = useMemo(() => {
    let result = data;

    // 1. Status Filter
    if (statusFilter !== 'all') {
      result = result.filter(b => b.status === statusFilter);
    }

    // 2. Date Filter
    if (applyTodayFilter) {
      const exactMatches = result.filter(b => isDateToday(b.booking_date));
      if (exactMatches.length === 0 && result.length > 0) {
        setUsedFallback(true);
      } else {
        setUsedFallback(false);
        result = exactMatches;
      }
    }

    // 3. String Search Filter
    if (q) {
      const query = q.toLowerCase();
      result = result.filter(b => {
        const t = (b.customer_name + b.customer_phone + (b.project_name || '') + (b.unit_no || '') + b.id).toLowerCase();
        return t.includes(query);
      });
    }

    return result;
  }, [data, q, statusFilter, applyTodayFilter]);

  const canCreate = hasPermission('bookings', 'create');
  const canDelete = hasPermission('bookings', 'delete');

  const clearFilters = () => {
    setStatusFilter('all');
    setApplyTodayFilter(false);
    setUsedFallback(false);
    setQ('');
    navigate('.', { replace: true, state: null });
  };

  const todayUIString = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const stats = [
    { label: 'Active', value: data.filter(b => b.status === 'Active').length, icon: 'CheckCircle2', color: 'text-emerald-600' },
    { label: 'Cancelled', value: data.filter(b => b.status === 'Cancelled').length, icon: 'XCircle', color: 'text-rose-600' },
    { label: 'Completed', value: data.filter(b => b.status === 'Completed').length, icon: 'Trophy', color: 'text-indigo-600' },
    { label: 'Total Value', value: `₹${(data.reduce((s, b) => s + (b.agreed_value || 0), 0) / 10_000_000).toFixed(2)} Cr`, icon: 'IndianRupee', color: 'text-brand-gold' },
  ];

  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-gradient-to-r from-[#0F1F38] via-[#1B3A5C] to-[#2A4F7A] text-white p-5 lg:p-6">
        <div className="flex items-center gap-3 mb-2">
          <Icons.BookOpen className="w-5 h-5 text-brand-gold" />
          <div className="text-xs uppercase tracking-[0.25em] text-brand-gold">Customer Conversion</div>
        </div>
        <h2 className="font-display text-2xl lg:text-3xl font-bold">Bookings</h2>
        <p className="text-slate-300 text-sm mt-1 max-w-xl">Convert leads to customers — KYC, unit lock, and automated payment schedule generation, all in one place.</p>
      </div>

      {/* Dynamic Filter Banner */}
      {applyTodayFilter && (
        <div className={`p-3 border rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-medium animate-in fade-in zoom-in duration-300 ${usedFallback ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
          <div className="flex items-center gap-2">
            <Icons.Filter className={`w-4 h-4 shrink-0 ${usedFallback ? 'text-amber-600' : 'text-blue-600'}`} />
            <span>
              {usedFallback ? (
                <>No explicit matches for today's date in dummy data. Falling back to show <strong>{statusFilter === 'all' ? 'All' : statusFilter}</strong> bookings.</>
              ) : (
                <>Showing <strong>{statusFilter === 'all' ? 'All' : statusFilter}</strong> bookings created exactly on <strong>{todayUIString}</strong>.</>
              )}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters} 
            className={`h-7 font-bold uppercase tracking-wider text-[10px] shrink-0 bg-white shadow-sm ${usedFallback ? 'text-amber-700 hover:bg-amber-100 hover:text-amber-900' : 'text-blue-700 hover:bg-blue-100 hover:text-blue-900'}`}
          >
            Clear Filters
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {stats.map(s => {
          const Icon = Icons[s.icon];
          return (
            <Card key={s.label} className="border-slate-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide">{s.label}</div>
                  <div className="font-display text-2xl font-bold mt-1">{s.value}</div>
                </div>
                <Icon className={`w-7 h-7 ${s.color}`} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search by customer, phone, unit, project..." value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Cancelled">Cancelled</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          {canCreate && (
            <BookingFormDialog
              onSaved={fetchAll}
              trigger={
                <Button data-testid="new-booking-btn" className="bg-brand-navy hover:bg-[#13294A] text-white">
                  <Icons.Plus className="w-4 h-4 mr-1.5" /> New Booking
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Booking</th>
                <th className="px-4 py-3 hidden md:table-cell">Project / Unit</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 hidden lg:table-cell">Loan</th>
                <th className="px-4 py-3 text-right">Value</th>
                <th className="px-4 py-3 hidden xl:table-cell">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}><td colSpan={7} className="p-2"><Skeleton className="h-10 w-full" /></td></tr>
              ))}
              {!loading && filtered.map(b => (
                <tr key={b.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`booking-row-${b.id}`} onClick={() => setActiveBookingId(b.id)}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{b.customer_name}</div>
                    <div className="text-[11px] text-slate-500">{b.id} • {b.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="text-slate-800">{b.project_name}</div>
                    <div className="text-[11px] text-slate-500">Unit {b.unit_no}</div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline" className={STAGE_COLOR[b.status]}>{b.status}</Badge></td>
                  <td className="px-4 py-3 text-slate-700 hidden lg:table-cell text-xs">{b.loan_status || '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">{inr(b.agreed_value)}</td>
                  <td className="px-4 py-3 text-slate-600 hidden xl:table-cell text-xs">{b.booking_date}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Download confirmation PDF"
                        data-testid={`pdf-${b.id}`}
                        onClick={async () => {
                          try {
                            const r = await (await import('../api/client')).default.get(`/bookings/${b.id}/confirmation.pdf`, { responseType: 'blob' });
                            const url = URL.createObjectURL(r.data);
                            window.open(url, '_blank', 'noopener,noreferrer');
                            setTimeout(() => URL.revokeObjectURL(url), 60_000);
                          } catch { toast.error('Failed to generate PDF'); }
                        }}
                      >
                        <Icons.FileDown className="w-4 h-4 text-brand-gold" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Open" onClick={() => setActiveBookingId(b.id)}>
                        <Icons.ExternalLink className="w-4 h-4" />
                      </Button>
                      {canDelete && (
                        <DeleteButton
                          title={`Delete booking ${b.id}?`}
                          description="The unit will be released and the lead status reset. Installments and documents will be deleted."
                          onConfirm={async () => {
                            try { await bookingsApi.remove(b.id); toast.success('Booking deleted'); fetchAll(); }
                            catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                          }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">
                    <div className="flex flex-col items-center justify-center">
                      <Icons.SearchX className="w-10 h-10 mb-2 opacity-50" />
                      <span className="italic font-medium">No bookings found matching current filters.</span>
                      {(q || statusFilter !== 'all' || applyTodayFilter) && (
                        <Button variant="link" onClick={clearFilters} className="mt-2 text-brand-navy">
                          Show all bookings instead
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <BookingDrawer
        bookingId={activeBookingId}
        open={!!activeBookingId}
        onOpenChange={(v) => !v && setActiveBookingId(null)}
        onChanged={fetchAll}
      />
    </div>
  );
};

export default Bookings;